
import { useCallback, useRef } from "react";
import { fetchArticlesPaged } from "@/store/news-feed/feed-content-management/feed-content-management.async-actions";
import useFeedContentManagementStore from "@/store/news-feed/feed-content-management/feed-content-management.slice";
import { trimKeyMessageName } from "@/store/news-feed/key-messages/key-message.selectors";
import useKeyMessageStore from "@/store/news-feed/key-messages/key-message.slice";
import { useAnalysisParamsContext } from "@/store/news-feed/params/analysis-params.context";
import { useAnalysisParamsForQueries } from "@/store/news-feed/params/useAnalysisParamsForQueries";
import { ExpandedSearchEditor } from "../ui/tiptap-editor/TiptapEditor";
import ArticlesContainer from "./article-content/ArticlesContainer";
import FeedMetrics from "./feed-metrics/FeedMetrics";
import NewsFeedVolumeGraph from "./feed-volume-graph/NewsFeedVolumeGraph";
import NewsFeedModals from "./modals/NewsFeedModals";
import ArticleContentControlPanel from "./news-feed-control-panels/articles-control-panel/ArticleContentControlPanel";

const NewsFeedContent = () => {
  const analysisParams = useAnalysisParamsContext();

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const isAdvancedSearch = useFeedContentManagementStore(
    (state) => state.isAdvancedSearch,
  );
  const setIsAdvancedSearch = useFeedContentManagementStore(
    (state) => state.setIsAdvancedSearch,
  );
  const searchTerm = useFeedContentManagementStore((state) => state.searchTerm);
  const setSearchTerm = useFeedContentManagementStore(
    (state) => state.setSearchTerm,
  );
  const setKeyMessageName = useKeyMessageStore(
    (state) => state.setKeyMessageName,
  );
  const setKeyMessageSearchTerm = useKeyMessageStore(
    (state) => state.setSearchTerm,
  );
  const setSaveSearchKeyMessageModalOpen = useKeyMessageStore(
    (state) => state.setSaveSearchKeyMessageModalOpen,
  );

  const { feedId, startDate, endDate, selectedFilters } =
    useAnalysisParamsForQueries();
  const resetState = useFeedContentManagementStore((state) => state.resetState);
  const isFetchingArticles = useFeedContentManagementStore(
    (state) => state.isFetchingArticles,
  );

  const handleSearch = async (searchValue: string) => {
    // Update searchTerm in store
    setSearchTerm(searchValue);

    if (!feedId || !startDate || !endDate) return;

    // Reset state to clear current articles
    resetState();

    // Fetch articles with the new search term
    await fetchArticlesPaged(
      feedId,
      startDate,
      endDate,
      selectedFilters,
      undefined, // No pagination for initial search
      false, // Not appending
    );
  };

  /**
   * Callback ref that captures the scrollable parent element
   * This ensures the intersection observer gets the correct root element
   */
  const handleRefCallback = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      const scrollableParent = node.parentElement as HTMLDivElement | null;
      const targetElement = scrollableParent || node;
      scrollContainerRef.current = targetElement;
    }
  }, []);

  return (
    <>
      <NewsFeedModals />
      <main className="min-h-screen" ref={handleRefCallback}>
        <div className="px-4 max-w-[964px] min-w-[964px] mx-auto">
          <FeedMetrics />
          <NewsFeedVolumeGraph />
          {isAdvancedSearch && (
            <ExpandedSearchEditor
              handleSearch={handleSearch}
              handleExpand={() => {
                setIsAdvancedSearch(false);
              }}
              handleSave={(editor) => {
                setKeyMessageName(trimKeyMessageName(editor.getText()));
                setKeyMessageSearchTerm(editor.getText());
                setSaveSearchKeyMessageModalOpen(true);
              }}
              content={searchTerm}
              isSearching={isFetchingArticles && !!searchTerm}
            />
          )}
          <ArticleContentControlPanel
            stickyTopOffset={analysisParams?.stickyHeaderHeight ?? 0}
            handleSearch={handleSearch}
          />
          <ArticlesContainer scrollContainerRef={scrollContainerRef} />
        </div>
      </main>
    </>
  );
};

export default NewsFeedContent;
